import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Heart, MessageCircle, Repeat2, UserPlus, AtSign, MessageSquare, Bell, BellOff, Trash2, FilePlus } from 'lucide-react'
import webPushService, { WebNotification } from '../services/webPushService'

export default function Notifications() {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<WebNotification[]>([])
  const [filter, setFilter] = useState<'all' | 'unread'>('all')
  const [permState, setPermState] = useState<NotificationPermission | 'unsupported'>('default')
  const [enablingPush, setEnablingPush] = useState(false)

  useEffect(() => {
    // Detect permission state
    if (!('Notification' in window)) {
      setPermState('unsupported')
    } else {
      setPermState(Notification.permission)
    }

    reload()

    // Subscribe to incoming FCM foreground messages
    const unsub = webPushService.onNotification(() => reload())

    // Mark all as read after 2 seconds
    const timer = setTimeout(() => {
      webPushService.markAllAsRead()
      reload()
    }, 2000)

    return () => {
      unsub()
      clearTimeout(timer)
    }
  }, [])

  const reload = () => setNotifications(webPushService.getNotifications())

  const unreadCount = notifications.filter(n => !n.read).length
  const filtered = filter === 'unread' ? notifications.filter(n => !n.read) : notifications

  const handleEnableNotifications = async () => {
    setEnablingPush(true)
    await webPushService.requestPermissionAndGetToken()
    setPermState(Notification.permission)
    setEnablingPush(false)
  }

  const handleClearAll = () => {
    if (confirm('Clear all notifications?')) {
      webPushService.clearAll()
      reload()
    }
  }

  const getIcon = (type: WebNotification['type']) => {
    switch (type) {
      case 'like':     return <Heart className="w-8 h-8 text-red-500 fill-red-500" />
      case 'comment':  return <MessageCircle className="w-8 h-8 text-blue-500" />
      case 'repost':   return <Repeat2 className="w-8 h-8 text-green-500" />
      case 'follow':   return <UserPlus className="w-8 h-8 text-purple-500" />
      case 'mention':  return <AtSign className="w-8 h-8 text-orange-500" />
      case 'reply':    return <MessageSquare className="w-8 h-8 text-blue-500" />
      case 'new_post': return <FilePlus className="w-8 h-8 text-onrecord-orange" />
      default:         return <Bell className="w-8 h-8 text-gray-400" />
    }
  }

  const getMessage = (n: WebNotification) => {
    switch (n.type) {
      case 'like':     return 'liked your post'
      case 'comment':  return `commented: ${n.content || ''}`
      case 'repost':   return 'reposted your post'
      case 'follow':   return 'started following you'
      case 'mention':  return 'mentioned you in a post'
      case 'reply':    return `replied: ${n.content || ''}`
      case 'new_post': return 'shared a new post'
      default:         return n.content || 'sent you a notification'
    }
  }

  const handleClick = (n: WebNotification) => {
    webPushService.markAsRead(n.id)
    reload()
    if (n.type === 'follow' && n.userId) {
      navigate(`/profile/${n.userId}`)
    } else if (n.postId) {
      navigate(`/post/${n.postId}`)
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 bg-white bg-opacity-90 backdrop-blur-sm border-b border-gray-200 z-10">
        <div className="px-4 py-3">
          <h1 className="text-xl font-bold">Notifications</h1>
        </div>
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setFilter('all')}
            className={`flex-1 py-3 text-center font-semibold transition-colors ${
              filter === 'all' ? 'text-black border-b-4 border-onrecord-orange' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`flex-1 py-3 text-center font-semibold transition-colors ${
              filter === 'unread' ? 'text-black border-b-4 border-onrecord-orange' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            Unread {unreadCount > 0 && `(${unreadCount})`}
          </button>
        </div>
      </div>

      {/* Enable notifications banner */}
      {permState === 'default' && (
        <div className="mx-4 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
          <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">Enable push notifications</p>
            <p className="text-xs text-amber-700 mt-0.5">Get notified when someone likes, comments, or follows you — even when the tab is closed.</p>
          </div>
          <button
            onClick={handleEnableNotifications}
            disabled={enablingPush}
            className="flex-shrink-0 bg-onrecord-orange text-white text-sm font-semibold px-3 py-1.5 rounded-lg disabled:opacity-50"
          >
            {enablingPush ? 'Enabling…' : 'Enable'}
          </button>
        </div>
      )}

      {permState === 'denied' && (
        <div className="mx-4 mt-4 p-4 bg-gray-50 border border-gray-200 rounded-xl flex items-start gap-3">
          <BellOff className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-gray-500">
            Notifications are blocked. To enable them, click the lock icon in your browser's address bar and allow notifications for onrecord.live.
          </p>
        </div>
      )}

      {/* Actions bar */}
      {notifications.length > 0 && (
        <div className="border-b border-gray-200 px-4 py-2 flex items-center justify-end">
          <button
            onClick={handleClearAll}
            className="text-sm text-red-500 hover:underline flex items-center gap-1"
          >
            <Trash2 className="w-4 h-4" />
            Clear All
          </button>
        </div>
      )}

      {/* List */}
      <div>
        {filtered.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-6xl mb-4">🔔</div>
            <h2 className="text-2xl font-bold mb-2">No notifications</h2>
            <p className="text-gray-500">
              {filter === 'unread' ? "You're all caught up!" : "When you get notifications, they'll show up here"}
            </p>
          </div>
        ) : (
          filtered.map((n) => (
            <div
              key={n.id}
              onClick={() => handleClick(n)}
              className={`flex gap-3 p-4 border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer ${!n.read ? 'bg-blue-50' : ''}`}
            >
              <div className="flex-shrink-0 mt-1">{getIcon(n.type)}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3">
                  <img
                    src={n.userAvatar || '/logo.svg'}
                    alt={n.userName}
                    className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
                    onError={(e) => { (e.target as HTMLImageElement).src = '/logo.svg' }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-bold">{n.userName}</span>
                      {' '}
                      <span className="text-gray-700">{getMessage(n)}</span>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDistanceToNow(new Date(n.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                  {!n.read && <div className="w-2 h-2 bg-onrecord-orange rounded-full flex-shrink-0 mt-2" />}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
